/* ═══════════════════════════════════════════════════════════════
   useProjectSyncFeed — a project's master updates, live (SYNC_PLAN §2.6).
   Shared by the Master updates widget and the Specs widget's badges.
   Re-fetches when the server broadcasts `sync_feed` (or the copy itself
   changes) and on every sync-complete.
   ═══════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useState } from 'react';
import { SyncService, type SyncFeed } from '../../../services/syncService';
import { useTableSync } from '../../../hooks/useTableSync';

export interface ProjectSyncFeed {
  feed: SyncFeed | null;
  status: 'loading' | 'ready' | 'error';
  error?: string;
  reload: () => void;
}

export function useProjectSyncFeed(projectId: string): ProjectSyncFeed {
  const [feed, setFeed] = useState<SyncFeed | null>(null);
  const [status, setStatus] = useState<ProjectSyncFeed['status']>('loading');
  const [error, setError] = useState<string | undefined>();
  const [rev, setRev] = useState(0);
  const reload = useCallback(() => setRev((n) => n + 1), []);

  useEffect(() => {
    if (!projectId) return;
    let live = true;
    SyncService.feedList({ projectId })
      .then((res) => {
        if (!live) return;
        setFeed(res);
        setError(undefined);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (!live) return;
        setError(err instanceof Error ? err.message : 'Could not load master updates');
        setStatus('error');
      });
    return () => {
      live = false;
    };
  }, [projectId, rev]);

  useTableSync(['sync_feed', 'project_specs'], reload);
  return { feed, status, error, reload };
}
